// ---------------------------------------------------------------------------
// Request Response Cache
//
// In-memory GET response cache with two levers only:
//   1. LRU cap (maxEntries, default 128) — bounds RAM without timers.
//   2. Full map.clear() via clearRequestCache() — call on any context
//      change (login, logout, project switch…).
//
// No per-entry TTL, no epoch baked into the hash: map.clear() physically
// removes all entries, making stale-key tricks unnecessary.
//
// Covers both fetch (installRequestCache wraps window.fetch) and XHR (hooked
// in xhrPatch.js via getXhrCache / putXhrCache).
//
// Self-installs at import time (side effect). Import order in main.jsx matters:
// must come after requestBatcher.js so the cache wraps the batcher.
// Disable at runtime: set VITE_ORBIT_REQUEST_CACHE=false in .env.local.
// ---------------------------------------------------------------------------

import { getPathname } from './csrf.js';

// ---------------------------------------------------------------------------
// Default cacheable patterns — same footprint as BATCHABLE_PATTERNS;
// these endpoints are idempotent read-only GETs safe to serve from cache.
// ---------------------------------------------------------------------------

export const CACHEABLE_PATTERNS = [
  '/decoupled-api/', // all decoupled_toolbox entity endpoints
  '/api/user/info',  // current-user endpoint
];

let _patterns = CACHEABLE_PATTERNS;
let _maxEntries = 128;

// Map used as LRU: insertion order = eviction order.
// On hit: delete then re-insert to move entry to "most recent" position.
const _cache = new Map();

// ---------------------------------------------------------------------------
// Eligibility
// ---------------------------------------------------------------------------

function isEligibleUrl(url) {
  if (!url || _patterns.length === 0) return false;
  const pathname = getPathname(url);
  if (!pathname) return false;
  return _patterns.some(p =>
    p instanceof RegExp ? p.test(pathname) : pathname.startsWith(p)
  );
}

/**
 * Returns true when a request is eligible for caching (GET + matching pattern).
 * Respects VITE_ORBIT_REQUEST_CACHE=false, and also the standard fetch
 * `cache: 'no-store'` / `'reload'` / `'no-cache'` opt-outs so a caller can
 * explicitly request a fresh trip to the backend (used by polling code).
 * Synchronous — only checks eligibility, no hashing.
 */
export function isEligible(url, method, cacheMode) {
  if (import.meta.env?.VITE_ORBIT_REQUEST_CACHE === 'false') return false;
  if (cacheMode === 'no-store' || cacheMode === 'reload' || cacheMode === 'no-cache') return false;
  return (method ?? 'GET').toUpperCase() === 'GET' && isEligibleUrl(url);
}

// ---------------------------------------------------------------------------
// Amorçage : les réponses inlinées par la génération statique
//
// Une page pré-générée (cf. services/ssg-renderer) porte, dans un
// `<script id="__ORBIT_SSG_DATA__">`, les réponses qui ont servi à la produire.
// Sans elles, le SPA qui reprend la main referait les trois requêtes en cascade
// (resolve → catalogue → page) et, comme React vide son conteneur au premier
// commit, le visiteur verrait le contenu disparaître puis revenir.
//
// C'est un magasin SÉPARÉ du cache LRU, pour deux raisons :
//  - la clé du cache est un hachage (méthode + URL absolue + corps + en-têtes),
//    que le générateur ne peut pas reproduire — il ne connaît pas les en-têtes
//    que posera l'appelant ;
//  - deux des trois réponses ne sont PAS cachables (`/api/builder/*` n'est pas
//    dans CACHEABLE_PATTERNS, et le catalogue est demandé en `no-store`). Les y
//    rendre éligibles changerait leur fraîcheur pour toute l'application.
//
// À USAGE UNIQUE : la première lecture consomme l'entrée. C'est ce qui rend la
// réduction du catalogue inoffensive — une navigation dans le SPA repart du
// réseau, donc du catalogue complet.
// ---------------------------------------------------------------------------

const _seed = new Map();

/**
 * Charge les réponses inlinées, indexées par chemin (`/api/...?...`).
 * @param {Record<string, string>} entries corps de réponse, en texte
 */
export function primeRequestCache(entries) {
  if (!entries || typeof entries !== 'object') return;
  for (const [path, body] of Object.entries(entries)) {
    if (typeof body === 'string') _seed.set(path, body);
  }
  if (import.meta.env.DEV) {
    console.debug(`[requestCache] SSG prime (${_seed.size} réponses)`, [..._seed.keys()]);
  }
}

/** Rend le corps amorcé pour cette URL, et le retire. */
function takeSeed(url) {
  if (_seed.size === 0) return undefined;
  let path;
  try {
    const u = new URL(url, location.origin);
    if (u.origin !== location.origin) return undefined;
    path = u.pathname + u.search;
  } catch { return undefined; }
  const body = _seed.get(path);
  if (body === undefined) return undefined;
  _seed.delete(path);
  return body;
}

// ---------------------------------------------------------------------------
// Cache key: SHA-256 of (method + absolute URL + body + sorted headers)
//
// The epoch is NOT part of the hash: clearRequestCache() calls
// map.clear(), so old entries are physically gone and stale-key avoidance
// via the epoch would be redundant overhead.
// ---------------------------------------------------------------------------

async function hashRequest(url, body, headersObj) {
  const absUrl = (() => {
    try { return new URL(url, location.origin).href; } catch { return url; }
  })();
  const sortedHeaders = Object.entries(headersObj ?? {})
    .map(([k, v]) => [k.toLowerCase(), v])
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([k, v]) => `${k}:${v}`)
    .join('\n');
  const raw = `GET\n${absUrl}\n${body ?? ''}\n${sortedHeaders}`;
  const encoded = new TextEncoder().encode(raw);
  const buffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// ---------------------------------------------------------------------------
// LRU helpers
// ---------------------------------------------------------------------------

function lruGet(key) {
  if (!_cache.has(key)) return undefined;
  const value = _cache.get(key);
  _cache.delete(key);
  _cache.set(key, value); // move to most-recent
  return value;
}

function lruSet(key, value) {
  if (_cache.has(key)) _cache.delete(key);
  _cache.set(key, value);
  if (_cache.size > _maxEntries) {
    // Evict oldest (first entry in insertion order).
    _cache.delete(_cache.keys().next().value);
  }
}

// ---------------------------------------------------------------------------
// Fetch cache API
// ---------------------------------------------------------------------------

/** Normalizes `init.headers` (Headers instance or plain object) for hashing. */
function headersOf(init) {
  return init?.headers instanceof Headers
    ? Object.fromEntries(init.headers.entries())
    : (init?.headers ?? {});
}

// ---------------------------------------------------------------------------
// On stocke les OCTETS d'une réponse, jamais un objet Response.
//
// `Response.clone()` ne copie pas le corps : il DÉDOUBLE le flux de la requête
// d'origine. Si l'appelant annule ensuite cette requête — un `AbortController`
// libéré au démontage d'un composant, geste banal en React —, le flux du clone
// gardé en cache est mis en erreur avec elle. Chaque HIT ultérieur rejetait
// alors `AbortError`, y compris pour des appelants qui ne passent AUCUN signal :
// une seule annulation empoisonnait l'URL pour toute la session.
//
// Vécu sur la liste des sites du builder : ouverte une fois elle s'affichait,
// rouverte elle revenait vide. On lit donc le corps au moment de la mise en
// cache (le clone le tamponnait de toute façon), et on reconstruit une réponse
// neuve à chaque lecture.
// ---------------------------------------------------------------------------

/**
 * Returns a fresh Response rebuilt from cache, or undefined on miss.
 * @param {string} url
 * @param {RequestInit} [init]
 * @returns {Promise<Response|undefined>}
 */
export async function getFetchCache(url, init) {
  if (!isEligible(url, init?.method, init?.cache)) return undefined;
  const key = await hashRequest(url, init?.body, headersOf(init));
  const entry = lruGet(key)?.fetchEntry;
  if (import.meta.env.DEV) {
    console.debug(`[requestCache] fetch ${entry ? 'HIT ' : 'MISS'}`, url);
  }
  if (!entry) return undefined;
  // `slice(0)` : une copie par lecture — un ReadableStream ne se relit pas, et
  // deux appelants simultanés liraient le même tampon.
  return new Response(entry.body ? entry.body.slice(0) : null, {
    status: entry.status,
    statusText: entry.statusText,
    headers: entry.headers,
  });
}

/**
 * Reads a response clone into memory and stores it.
 * The caller must pass a clone that has not yet been consumed.
 * Silently skips non-2xx responses, and bodies that can no longer be read
 * (request aborted meanwhile) — better no entry than one that fails forever.
 * @param {string} url
 * @param {RequestInit} [init]
 * @param {Response} responseClone - an unconsumed Response clone
 */
export async function putFetchCache(url, init, responseClone) {
  if (!isEligible(url, init?.method, init?.cache) || !responseClone.ok) return;
  let body;
  try {
    body = await responseClone.arrayBuffer();
  }
  catch {
    return;
  }
  const key = await hashRequest(url, init?.body, headersOf(init));
  lruSet(key, {
    fetchEntry: {
      // 204/205 n'ont pas de corps : `new Response(<vide>, {status: 204})`
      // lèverait à la relecture.
      body: body.byteLength ? body : null,
      status: responseClone.status,
      statusText: responseClone.statusText,
      headers: [...responseClone.headers.entries()],
    },
  });
  if (import.meta.env.DEV) {
    console.debug(`[requestCache] fetch STORE (${_cache.size}/${_maxEntries})`, url);
  }
}

// ---------------------------------------------------------------------------
// XHR cache API
//
// XHR GET requests from axios carry no custom body and only default headers,
// so the key is URL only. The stored value matches the batcher's
// `part` shape: { headers: Record<string, string[]>, body: string }.
// This lets xhrPatch.js replay cache hits via the existing resolveXhr() path.
// ---------------------------------------------------------------------------

/**
 * Returns the cached XHR part, or undefined on miss.
 * @param {string} url
 * @returns {Promise<{headers: Object, body: string}|undefined>}
 */
export async function getXhrCache(url) {
  // Amorce SSG d'abord, et à usage unique. Portée réelle de CE chemin : les URLs
  // déjà jugées éligibles par xhrPatch, c'est-à-dire `/decoupled-api/` — soit les
  // sources de blocs qu'axios demande en XHR. Les deux grosses réponses non
  // éligibles de la cascade (/api/builder/…, catalogue en no-store) sont servies
  // par le chemin fetch, en tête de `installRequestCache`.
  const seeded = takeSeed(url);
  if (seeded !== undefined) {
    return { headers: { status: ['200'], 'content-type': ['application/json'] }, body: seeded };
  }
  if (!isEligible(url, 'GET')) return undefined;
  const key = await hashRequest(url, '', {});
  const hit = lruGet(key)?.xhrPart;
  if (import.meta.env.DEV) {
    console.debug(`[requestCache] xhr  ${hit ? 'HIT ' : 'MISS'}`, url);
  }
  return hit;
}

/**
 * Stores an XHR response part in the cache. Silently skips non-2xx.
 * @param {string} url
 * @param {{ headers: Record<string, string[]>, body: string }} part
 */
export async function putXhrCache(url, part) {
  if (!isEligible(url, 'GET')) return;
  const status = parseInt(part.headers?.status?.[0] ?? '0', 10);
  if (status < 200 || status >= 300) return;
  const key = await hashRequest(url, '', {});
  lruSet(key, { xhrPart: part });
  if (import.meta.env.DEV) {
    console.debug(`[requestCache] xhr  STORE (${_cache.size}/${_maxEntries})`, url);
  }
}

// ---------------------------------------------------------------------------
// Invalidation
// ---------------------------------------------------------------------------

/**
 * Clears the entire cache, instantly invalidating all cached entries.
 * Call after login, logout, or any server-side context change that makes
 * cached responses stale (project switch, impersonation, etc.).
 */
export function clearRequestCache() {
  if (import.meta.env.DEV) {
    console.debug(`[requestCache] CLEARED (${_cache.size} entries dropped)`);
  }
  _cache.clear();
}

/**
 * Namespaces whose WRITES invalidate the read cache.
 *
 * Wider than CACHEABLE_PATTERNS on purpose: what a mutation invalidates is
 * rarely reachable at the URL it was posted to. Archiving a CRM record posts
 * to /api/orbit/archive, yet what changes is the answer of
 * /decoupled-api/node/company/218 — with only the cacheable namespaces here,
 * the record page refetched itself and got its own stale entry back, so the
 * "Archivé — lecture seule" banner stayed up after unarchiving.
 *
 * /api/elasticsearch/ is deliberately absent: its POSTs are read refinements
 * (facets, pagination), fired on every filter click — they write nothing, and
 * clearing the cache there would empty it constantly for no reason.
 */
const INVALIDATING_PATTERNS = [...CACHEABLE_PATTERNS, '/api/orbit/'];

function isInvalidatingUrl(url) {
  const pathname = getPathname(url);
  if (!pathname) return false;
  return INVALIDATING_PATTERNS.some(p =>
    p instanceof RegExp ? p.test(pathname) : pathname.startsWith(p)
  );
}

/**
 * Invalidates the cache iff the (url, method) pair is a write to one of the
 * invalidating namespaces. Cheap, no-op on reads or unrelated URLs.
 *
 * Called from both the fetch wrapper (installRequestCache) and the XHR patch
 * (xhrPatch.js) so any mutating request — POST a new analysis_job, PATCH a
 * toy, DELETE a term, archive a record — drops the cache for the *whole*
 * cacheable namespace. A finer-grained invalidation would need a reverse
 * url→key index; full clear is acceptable here because the cacheable set is
 * tiny (decoupled API + /api/user/info) and these mutations are user-initiated,
 * not bursty.
 */
export function maybeInvalidateOnMutation(url, method) {
  if (!url) return;
  const m = (method ?? 'GET').toUpperCase();
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return;
  if (!isInvalidatingUrl(url)) return;
  clearRequestCache();
}

/**
 * Wraps window.fetch as the outermost cache layer.
 * Must be called explicitly from main.jsx, after installRequestBatcher(), so
 * the cache short-circuits before the batcher and the CSRF layer are reached.
 * Disable by setting VITE_ORBIT_REQUEST_CACHE=false in .env.local.
 */
export function installRequestCache() {
  // isEligible() already returns false when VITE_ORBIT_REQUEST_CACHE=false,
  // covering both fetch and XHR. We still skip the window.fetch wrap to avoid
  // a no-op wrapper in the chain.
  if (import.meta.env?.VITE_ORBIT_REQUEST_CACHE === 'false') return;

  const _inner = window.fetch.bind(window);

  window.fetch = async function cachedFetch(input, init) {
    const rawUrl = typeof input === 'string' ? input
      : (input instanceof Request ? input.url : null);
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');

    // L'amorce passe AVANT l'éligibilité : les deux réponses les plus coûteuses
    // de la cascade ne sont pas cachables (l'une hors namespace, l'autre demandée
    // en no-store), et ce sont précisément celles-là qu'il faut servir
    // sans aller-retour pour que la reprise du SPA ne clignote pas.
    if (rawUrl && (method ?? 'GET').toUpperCase() === 'GET') {
      const seeded = takeSeed(rawUrl);
      if (seeded !== undefined) {
        return new Response(seeded, { status: 200, headers: { 'content-type': 'application/json' } });
      }
    }

    if (rawUrl && isEligible(rawUrl, method, init?.cache)) {
      const cached = await getFetchCache(rawUrl, init);
      if (cached) return cached;

      const response = await _inner(input, init);
      if (response.ok) {
        // Clone before returning so the consumer can still read the body.
        // putFetchCache stores this clone and returns clones of it on future hits.
        putFetchCache(rawUrl, init, response.clone()); // async, fire-and-forget
      }
      return response;
    }

    // Mutating request on a cacheable namespace: pass through, but invalidate
    // the cache on 2xx so the next GET reflects the write. Without this, a
    // POST/PATCH followed by a refetch of a collection returns a stale list.
    const response = await _inner(input, init);
    if (response.ok) {
      maybeInvalidateOnMutation(rawUrl, method);
    }
    return response;
  };
}
